'use client';

import React, { useMemo, useRef, useEffect } from 'react';
import { useSession } from './SessionProvider';

// ─── Inter-Hemispheric Coherence Map ─────────────────────────────────────────
// Coherence measures phase synchrony between homologous electrode pairs.
// Reduced inter-hemispheric coherence is a validated Alzheimer's biomarker:
//   - Knott et al. (2000): coherence reduction in MCI/AD patients
//   - Jeong (2004): EEG coherence as AD progression marker
//   - Babiloni et al. (2006): reduced alpha/theta coherence in AD

// ─── Electrode Pair Definitions ──────────────────────────────────────────────
interface ElectrodePair {
  id: string;
  left: string;
  right: string;
  region: string;
  color: string;
  bandRelevance: string;
  clinicalNote: string;
}

const ELECTRODE_PAIRS: ElectrodePair[] = [
  {
    id: 'frontal',
    left: 'F3',
    right: 'F4',
    region: 'Frontal',
    color: '#38bdf8',
    bandRelevance: 'Alpha / Theta',
    clinicalNote: 'Frontal asymmetry linked to depression & executive dysfunction',
  },
  {
    id: 'central',
    left: 'C3',
    right: 'C4',
    region: 'Central',
    color: '#34d399',
    bandRelevance: 'Beta / Gamma',
    clinicalNote: 'Sensorimotor coherence — disrupted in motor disorders',
  },
  {
    id: 'parietal',
    left: 'P3',
    right: 'P4',
    region: 'Parietal',
    color: '#fbbf24',
    bandRelevance: 'Alpha',
    clinicalNote: 'Parietal coherence loss is an early Alzheimer\'s indicator',
  },
  {
    id: 'temporal',
    left: 'T7',
    right: 'T8',
    region: 'Temporal',
    color: '#F97316',
    bandRelevance: 'Theta / Alpha',
    clinicalNote: 'Temporal coherence reflects memory network integrity',
  },
  {
    id: 'prefrontal',
    left: 'Fp1',
    right: 'Fp2',
    region: 'Prefrontal',
    color: '#a78bfa',
    bandRelevance: 'Gamma / Beta',
    clinicalNote: 'Working memory and executive function connectivity',
  },
];

// ─── Coherence Interpretation ─────────────────────────────────────────────────
function getCoherenceStatus(coherence: number): {
  label: string;
  color: string;
  bgColor: string;
  borderColor: string;
  description: string;
} {
  if (coherence >= 0.78) return {
    label: 'Strong Coherence',
    color: '#34d399',
    bgColor: 'rgba(52,211,153,0.08)',
    borderColor: 'rgba(52,211,153,0.3)',
    description: 'Healthy inter-hemispheric synchrony',
  };
  if (coherence >= 0.60) return {
    label: 'Moderate Coherence',
    color: '#38bdf8',
    bgColor: 'rgba(56,189,248,0.08)',
    borderColor: 'rgba(56,189,248,0.3)',
    description: 'Normal connectivity range',
  };
  if (coherence >= 0.45) return {
    label: 'Reduced Coherence',
    color: '#f59e0b',
    bgColor: 'rgba(245,158,11,0.08)',
    borderColor: 'rgba(245,158,11,0.3)',
    description: 'Below-average synchrony — monitor trends',
  };
  return {
    label: 'Low Coherence',
    color: '#ef4444',
    bgColor: 'rgba(239,68,68,0.08)',
    borderColor: 'rgba(239,68,68,0.3)',
    description: 'Significant connectivity reduction — clinical review advised',
  };
}

// ─── Brain Connectivity Canvas ────────────────────────────────────────────────
function ConnectivityCanvas({
  coherence,
  pairCoherences,
}: {
  coherence: number;
  pairCoherences: { id: string; value: number; color: string }[];
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);
  const tRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const W = canvas.width;
    const H = canvas.height;
    const cx = W / 2;
    const cy = H / 2;

    // Left hemisphere centre, right hemisphere centre
    const LX = cx - W * 0.22;
    const RX = cx + W * 0.22;
    const hemisphereR = W * 0.18;

    // Electrode positions on each hemisphere (y-axis spread)
    const pairYPositions: Record<string, number> = {
      prefrontal: cy - hemisphereR * 0.72,
      frontal:    cy - hemisphereR * 0.38,
      central:    cy,
      parietal:   cy + hemisphereR * 0.38,
      temporal:   cy + hemisphereR * 0.72,
    };

    const draw = () => {
      tRef.current += 0.018;
      const t = tRef.current;
      ctx.clearRect(0, 0, W, H);

      // ── Draw hemispheres ──
      const drawHemisphere = (x: number, side: 'left' | 'right') => {
        // Outer glow
        const grd = ctx.createRadialGradient(x, cy, 0, x, cy, hemisphereR * 1.4);
        grd.addColorStop(0, `rgba(30,58,95,${0.15 + coherence * 0.1})`);
        grd.addColorStop(1, 'rgba(30,58,95,0)');
        ctx.beginPath();
        ctx.arc(x, cy, hemisphereR * 1.4, 0, Math.PI * 2);
        ctx.fillStyle = grd;
        ctx.fill();

        // Hemisphere circle
        ctx.beginPath();
        ctx.arc(x, cy, hemisphereR, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(30,58,95,0.7)`;
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.fillStyle = 'rgba(11,22,40,0.6)';
        ctx.fill();

        // Label
        ctx.fillStyle = 'rgba(148,163,184,0.6)';
        ctx.font = '10px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(side === 'left' ? 'LEFT' : 'RIGHT', x, cy + hemisphereR + 16);

        // Electrode dots
        pairCoherences.forEach(pair => {
          const y = pairYPositions[pair.id] ?? cy;
          const pulse = (Math.sin(t * 2.5 + pair.value * 5) + 1) / 2;
          const r = 4 + pulse * 2;

          // Glow
          const eg = ctx.createRadialGradient(x, y, 0, x, y, r * 3);
          eg.addColorStop(0, `${pair.color}55`);
          eg.addColorStop(1, 'transparent');
          ctx.beginPath();
          ctx.arc(x, y, r * 3, 0, Math.PI * 2);
          ctx.fillStyle = eg;
          ctx.fill();

          // Dot
          ctx.beginPath();
          ctx.arc(x, y, r, 0, Math.PI * 2);
          ctx.fillStyle = pair.color;
          ctx.fill();
        });
      };

      drawHemisphere(LX, 'left');
      drawHemisphere(RX, 'right');

      // ── Draw coherence arcs between hemispheres ──
      pairCoherences.forEach(pair => {
        const y = pairYPositions[pair.id] ?? cy;
        const coh = pair.value;

        // Bezier control point (arc above/below midline)
        const cpY = y - (cy - y) * 0.3 - 20;

        // Animated signal traveling along the arc
        const signalT = (Math.sin(t * 1.8 + pair.value * 3) + 1) / 2;

        // Arc line
        ctx.beginPath();
        ctx.moveTo(LX, y);
        ctx.quadraticCurveTo(cx, cpY, RX, y);
        ctx.strokeStyle = `${pair.color}${Math.round(coh * 180).toString(16).padStart(2, '0')}`;
        ctx.lineWidth = 1 + coh * 2.5;
        ctx.stroke();

        // Traveling signal dot
        const bx = (1 - signalT) * (1 - signalT) * LX + 2 * (1 - signalT) * signalT * cx + signalT * signalT * RX;
        const by = (1 - signalT) * (1 - signalT) * y + 2 * (1 - signalT) * signalT * cpY + signalT * signalT * y;

        ctx.beginPath();
        ctx.arc(bx, by, 3 + coh * 2, 0, Math.PI * 2);
        ctx.fillStyle = pair.color;
        ctx.fill();

        // Glow on signal dot
        const sg = ctx.createRadialGradient(bx, by, 0, bx, by, 8);
        sg.addColorStop(0, `${pair.color}88`);
        sg.addColorStop(1, 'transparent');
        ctx.beginPath();
        ctx.arc(bx, by, 8, 0, Math.PI * 2);
        ctx.fillStyle = sg;
        ctx.fill();
      });

      // ── Overall coherence ring in centre ──
      const ringR = 22;
      const ringGrd = ctx.createRadialGradient(cx, cy, 0, cx, cy, ringR * 2);
      ringGrd.addColorStop(0, `rgba(249,115,22,${0.08 + coherence * 0.12})`);
      ringGrd.addColorStop(1, 'transparent');
      ctx.beginPath();
      ctx.arc(cx, cy, ringR * 2, 0, Math.PI * 2);
      ctx.fillStyle = ringGrd;
      ctx.fill();

      const sweepRad = coherence * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(cx, cy, ringR, -Math.PI / 2, -Math.PI / 2 + sweepRad);
      ctx.strokeStyle = coherence >= 0.6 ? '#34d399' : coherence >= 0.45 ? '#f59e0b' : '#ef4444';
      ctx.lineWidth = 3;
      ctx.stroke();

      ctx.fillStyle = 'rgba(148,163,184,0.8)';
      ctx.font = 'bold 11px monospace';
      ctx.textAlign = 'center';
      ctx.fillText(`${Math.round(coherence * 100)}%`, cx, cy + 4);

      animRef.current = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animRef.current);
  }, [coherence, pairCoherences]);

  return (
    <canvas
      ref={canvasRef}
      width={320}
      height={280}
      className="w-full max-w-[320px] mx-auto"
      style={{ imageRendering: 'crisp-edges' }}
    />
  );
}

// ─── Coherence Bar Row ────────────────────────────────────────────────────────
function CoherenceRow({ pair, value }: { pair: ElectrodePair; value: number }) {
  const pct = Math.round(value * 100);
  const isHealthy = value >= 0.6;
  const isWarning = value >= 0.45 && value < 0.6;

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ background: pair.color, boxShadow: `0 0 4px ${pair.color}` }} />
          <span className="text-[11px] font-semibold text-zinc-300">{pair.region}</span>
          <span className="text-[10px] text-zinc-600">{pair.left} ↔ {pair.right}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-zinc-500">{pair.bandRelevance}</span>
          <span
            className="text-[11px] font-mono font-bold"
            style={{ color: isHealthy ? '#34d399' : isWarning ? '#f59e0b' : '#ef4444' }}
          >
            {pct}%
          </span>
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-[#0d1f35] overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${pct}%`,
            background: isHealthy
              ? `linear-gradient(90deg, ${pair.color}88, ${pair.color})`
              : isWarning
              ? 'linear-gradient(90deg, #f59e0b88, #f59e0b)'
              : 'linear-gradient(90deg, #ef444488, #ef4444)',
            boxShadow: `0 0 4px ${isHealthy ? pair.color : isWarning ? '#f59e0b' : '#ef4444'}`,
          }}
        />
      </div>
      <p className="text-[10px] text-zinc-600 leading-tight">{pair.clinicalNote}</p>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ConnectivityCoherenceMap() {
  const { metrics } = useSession();
  const { interHemisphericCoherence, alphaPower, thetaPower, betaPower, gammaPower } = metrics;

  const coherence = interHemisphericCoherence / 100; // normalise 0–1
  const status = getCoherenceStatus(coherence);

  // Derive per-region coherence estimates from available band powers
  const pairCoherences = useMemo(() => ELECTRODE_PAIRS.map(pair => {
    let value: number;
    switch (pair.id) {
      case 'prefrontal': value = Math.min(1, coherence * 0.95 + gammaPower * 0.05); break;
      case 'frontal':    value = Math.min(1, coherence * 0.92 + alphaPower * 0.08); break;
      case 'central':    value = Math.min(1, coherence * 0.98 + betaPower * 0.02); break;
      case 'parietal':   value = Math.min(1, coherence * 0.90 + alphaPower * 0.10); break;
      case 'temporal':   value = Math.min(1, coherence * 0.88 + thetaPower * 0.12); break;
      default:           value = coherence;
    }
    return { id: pair.id, value, color: pair.color };
  }), [coherence, alphaPower, thetaPower, betaPower, gammaPower]);

  const avgCoherence = Math.round(coherence * 100);
  const weakestPair = pairCoherences.reduce((a, b) => a.value < b.value ? a : b);
  const strongestPair = pairCoherences.reduce((a, b) => a.value > b.value ? a : b);
  const weakestInfo = ELECTRODE_PAIRS.find(p => p.id === weakestPair.id);
  const strongestInfo = ELECTRODE_PAIRS.find(p => p.id === strongestPair.id);

  return (
    <div className="rounded-2xl overflow-hidden" style={{
      background: 'linear-gradient(160deg, rgba(11,22,40,0.95) 0%, rgba(6,13,26,0.98) 100%)',
      border: '1px solid rgba(30,58,95,0.5)',
    }}>
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-[#1E3A5F]/40">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.25)' }}
            >
              <span className="text-[16px]">🧠</span>
            </div>
            <div>
              <h3 className="text-[14px] font-bold text-white">Functional Connectivity Map</h3>
              <p className="text-[11px] text-zinc-500">Inter-Hemispheric Coherence · Alzheimer's Biomarker</p>
            </div>
          </div>
          <div
            className="px-3 py-1.5 rounded-lg text-[11px] font-semibold"
            style={{
              background: status.bgColor,
              border: `1px solid ${status.borderColor}`,
              color: status.color,
            }}
          >
            {status.label}
          </div>
        </div>
      </div>

      <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Left: Canvas visualisation */}
        <div className="flex flex-col gap-4">
          <ConnectivityCanvas coherence={coherence} pairCoherences={pairCoherences} />

          {/* Summary stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Overall', value: `${avgCoherence}%`, color: status.color, note: 'Mean coherence' },
              { label: 'Strongest', value: strongestInfo?.region ?? '—', color: strongestPair.color, note: `${Math.round(strongestPair.value * 100)}%` },
              { label: 'Weakest', value: weakestInfo?.region ?? '—', color: '#ef4444', note: `${Math.round(weakestPair.value * 100)}%` },
            ].map(stat => (
              <div
                key={stat.label}
                className="rounded-xl p-3 text-center"
                style={{ background: 'rgba(11,22,40,0.6)', border: '1px solid rgba(30,58,95,0.4)' }}
              >
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">{stat.label}</p>
                <p className="text-[13px] font-bold font-mono" style={{ color: stat.color }}>{stat.value}</p>
                <p className="text-[10px] text-zinc-600">{stat.note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Per-region coherence + research context */}
        <div className="flex flex-col gap-4">
          {/* Per-region bars */}
          <div
            className="rounded-xl p-4"
            style={{ background: 'rgba(11,22,40,0.6)', border: '1px solid rgba(30,58,95,0.4)' }}
          >
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-wider mb-3">
              Region-Pair Coherence
            </p>
            <div className="space-y-3">
              {ELECTRODE_PAIRS.map(pair => {
                const pc = pairCoherences.find(p => p.id === pair.id);
                return <CoherenceRow key={pair.id} pair={pair} value={pc?.value ?? coherence} />;
              })}
            </div>
          </div>

          {/* Alzheimer's biomarker context */}
          <div
            className="rounded-xl p-4"
            style={{
              background: 'linear-gradient(135deg, rgba(52,211,153,0.05) 0%, rgba(11,22,40,0.8) 100%)',
              border: '1px solid rgba(52,211,153,0.15)',
            }}
          >
            <div className="flex items-start gap-3">
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.2)' }}
              >
                <span className="text-[13px]">📊</span>
              </div>
              <div>
                <p className="text-[12px] font-bold text-[#34d399] mb-1">Alzheimer's Coherence Biomarker</p>
                <p className="text-[11px] text-zinc-400 leading-relaxed">
                  Inter-hemispheric coherence below <span className="text-[#f59e0b] font-semibold">60%</span> is
                  associated with MCI and early Alzheimer's disease. Parietal and temporal coherence loss
                  are the earliest detectable changes, often preceding clinical symptoms by 5–10 years.
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <div className="flex-1 h-1 rounded-full bg-[#0d1f35] overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{
                        width: `${avgCoherence}%`,
                        background: coherence >= 0.6 ? 'linear-gradient(90deg, #34d39988, #34d399)' : 'linear-gradient(90deg, #ef444488, #ef4444)',
                        boxShadow: `0 0 6px ${coherence >= 0.6 ? '#34d399' : '#ef4444'}`,
                      }}
                    />
                  </div>
                  <span className="text-[10px] font-mono" style={{ color: status.color }}>{avgCoherence}%</span>
                  <span className="text-[10px] text-zinc-600">/ 60% threshold</span>
                </div>
              </div>
            </div>
          </div>

          {/* Status description */}
          <div
            className="rounded-xl p-3"
            style={{ background: status.bgColor, border: `1px solid ${status.borderColor}` }}
          >
            <p className="text-[11px] font-semibold" style={{ color: status.color }}>{status.description}</p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-5 pb-4">
        <p className="text-[10px] text-zinc-600 text-center">
          ⚠ Research context only — not a clinical diagnostic tool. References: Knott et al. (2000); Jeong (2004) <em>Clin Neurophysiol</em>; Babiloni et al. (2006).
        </p>
      </div>
    </div>
  );
}
